<script setup lang="ts">
    import { Codemirror } from 'vue-codemirror';
    import { Extension, EditorState } from '@codemirror/state';
    import { sql, MSSQL, SQLDialect } from '@codemirror/lang-sql';
    import { vscodeLightInit } from '@uiw/codemirror-theme-vscode';
    import { EditorView } from '@codemirror/view';
    import { tags } from '@lezer/highlight';
    import { computed, onMounted, ref, watch } from 'vue';
    import { useSqlEditorStore } from '@/stores/sqlEditorStore.ts';

    type Props = {
        // The reference library the filter is evaluated against. Its columns are
        // offered as unqualified autocomplete suggestions.
        referenceLibraryName?: string;
    };
    const props = defineProps<Props>();

    const model = defineModel({
        type: String,
        required: false
    });

    const sqlEditStore = useSqlEditorStore();

    // The codemirror Extension type is deeply recursive, which TypeScript struggles
    // with, so we store the extensions as a plain object (see SqlEditControl.vue).
    const extensions = ref<object | null>(null);
    const loading = ref(true);

    // Non-editable prefix showing the query the filter is embedded in, so the user
    // sees their filter is a WHERE-clause against the reference library.
    const prefixText = computed(() =>
        props.referenceLibraryName
            ? `SELECT * FROM ${props.referenceLibraryName} WHERE -- Add filter below (OPTIONAL)`
            : '');

    // Shared, read-only styling for the prefix line (same dialect/theme as the editor).
    const prefixExtensions: object[] = [
        sql({
            dialect: SQLDialect.define({ ...MSSQL.spec, identifierQuotes: '["' }),
            upperCaseKeywords: true
        }),
        vscodeLightInit({ styles: [{ tag: tags.special(tags.string), color: '#0070c1' }] }),
        EditorView.editable.of(false),
        EditorState.readOnly.of(true),
        EditorView.lineWrapping,
        EditorView.theme({
            '.cm-gutters': { display: 'none' },
            '.cm-content': { fontStyle: 'italic' }
        })
    ];

    function buildExtensions(columns: string[]): object[] {
        const hasColumns = !!props.referenceLibraryName && columns.length > 0;
        const schema = hasColumns ? { [props.referenceLibraryName!]: columns } : undefined;

        return [
            sql({
                dialect: SQLDialect.define({ ...MSSQL.spec, identifierQuotes: '["' }), // Prefer [] over ""
                upperCaseKeywords: true,
                schema,
                // With a default table, the reference library's columns are suggested
                // unqualified inside the bare WHERE-expression the filter represents.
                defaultTable: hasColumns ? props.referenceLibraryName : undefined
            }),
            vscodeLightInit({ styles: [{ tag: tags.special(tags.string), color: '#0070c1' }] }), // Quoted identifiers should be light blue
            EditorView.lineWrapping,
            // Show at least two lines of editing space, and hide the line-number gutter.
            EditorView.theme({
                '.cm-content': { minHeight: '2.8em' },
                '.cm-gutters': { display: 'none' }
            })
        ];
    }

    async function refreshExtensions(): Promise<void> {
        loading.value = true;
        const fullSchema = await sqlEditStore.getLibrarySchema();
        const columns = (props.referenceLibraryName && fullSchema[props.referenceLibraryName]) || [];
        extensions.value = buildExtensions(columns);
        loading.value = false;
    }

    onMounted(refreshExtensions);

    watch(() => props.referenceLibraryName, refreshExtensions);
</script>

<template>
    <div
        v-if="!loading"
        class="sql-filter-edit-control">
        <codemirror
            class="sql-filter-edit-control__prefix"
            :autofocus="false"
            :model-value="prefixText"
            :extensions="prefixExtensions as Extension" />
        <codemirror
            v-model="model"
            class="sql-filter-edit-control__input"
            :autofocus="false"
            :indent-with-tab="true"
            :tab-size="2"
            :extensions="extensions as Extension" />
    </div>
</template>

<style scoped>
    .sql-filter-edit-control {
        border: 1px solid #dbdbdb;
        border-radius: 4px;
        overflow: hidden;
    }

    .sql-filter-edit-control__prefix {
        border-bottom: 1px solid #ededed;
        background-color: #f5f5f5;
    }

    /* The prefix is read-only, so suppress the active-line, selection and cursor
       styling that codemirror's basicSetup / theme would otherwise render. */
    .sql-filter-edit-control__prefix :deep(.cm-activeLine) {
        background-color: transparent !important;
    }

    .sql-filter-edit-control__prefix :deep(.cm-selectionBackground) {
        background: transparent !important;
    }

    .sql-filter-edit-control__prefix :deep(.cm-cursor) {
        display: none !important;
    }

    /* Suppress the active-line highlight on the editable filter too (cursor and
       selection are kept). */
    .sql-filter-edit-control__input :deep(.cm-activeLine) {
        background-color: transparent !important;
    }
</style>
